import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../../store/store';
import type { MessageEntity, TimelineEntity } from '../types';

// Unified timeline store (matches Preact/Zustand design)
// Holds both durable entities AND streaming updates in one place

/**
 * Type-safe helper to merge entity props when kinds match.
 * Uses generics to ensure type safety when merging props of the same entity type.
 * This function assumes both entities have the same `kind` and compatible props.
 *
 * @template T - The specific TimelineEntity type (e.g., MessageEntity, DebateResponseEntity)
 * @param existing - The existing entity in the store (WritableDraft from Immer)
 * @param updates - The new entity with updated props
 */
function mergeEntityProps<T extends TimelineEntity>(existing: T, updates: T): void {
  // Both entities have the same kind, so props are compatible
  // We merge the props using spread operator and assert the type is correct
  // This is safe because we've verified both entities have the same kind
  existing.props = { ...existing.props, ...updates.props } as T['props'];
}

// Note: We intentionally avoid a type-guard here because it can over-narrow
// the "else" branch to never. Instead we rely on a runtime kind check plus
// a minimal, safe assertion in the merge call below.

interface TimelineState {
  byConvId: Record<
    string,
    {
      byId: Record<string, TimelineEntity>;
      order: string[];
      displayMode?: 'chat' | 'ui';
    }
  >;
  // Current active conversation
  activeConvId: string | null;
}

const initialState: TimelineState = {
  byConvId: {},
  activeConvId: null,
};

function ensureConversation(state: TimelineState, convId: string) {
  const existing = state.byConvId[convId];
  if (existing) {
    existing.displayMode ??= 'chat';
    return existing;
  }
  const created: {
    byId: Record<string, TimelineEntity>;
    order: string[];
    displayMode: 'chat' | 'ui';
  } = { byId: {}, order: [], displayMode: 'chat' };
  state.byConvId[convId] = created;
  return created;
}

const timelineSlice = createSlice({
  name: 'timeline',
  initialState,
  reducers: {
    // Set active conversation
    setActiveConversation(state, action: PayloadAction<string>) {
      state.activeConvId = action.payload;
      ensureConversation(state, action.payload);
    },

    /**
     * Sets the display mode for a conversation.
     * - 'chat': Summary widgets are visible in chat timeline
     * - 'ui': Summary widgets are hidden in chat (shown in UI instead)
     */
    setConversationDisplayMode(
      state,
      action: PayloadAction<{ convId: string; displayMode: 'chat' | 'ui' }>
    ) {
      const { convId, displayMode } = action.payload;
      const conv = ensureConversation(state, convId);
      conv.displayMode = displayMode;
    },

    // Add entity to timeline
    addEntity(state, action: PayloadAction<{ convId: string; entity: TimelineEntity }>) {
      const { convId, entity } = action.payload;

      const conv = ensureConversation(state, convId);

      // Avoid duplicates
      if (conv.byId[entity.id]) return;

      conv.byId[entity.id] = entity;
      conv.order.push(entity.id);
    },

    // Add multiple entities at once
    addEntities(state, action: PayloadAction<{ convId: string; entities: TimelineEntity[] }>) {
      const { convId, entities } = action.payload;

      const conv = ensureConversation(state, convId);

      entities.forEach((entity) => {
        if (!conv.byId[entity.id]) {
          conv.byId[entity.id] = entity;
          conv.order.push(entity.id);
        }
      });
    },

    // Update entity props (for streaming text, progress, etc.)
    updateEntity(state, action: PayloadAction<{ convId: string; entityId: string; props: any }>) {
      const { convId, entityId, props } = action.payload;
      const entity = state.byConvId[convId]?.byId[entityId];

      if (entity) {
        entity.props = { ...entity.props, ...props };
      }
    },

    // Append a single item to an entity's array prop (e.g., chunks)
    appendEntityArrayItem(
      state,
      action: PayloadAction<{ convId: string; entityId: string; key: string; item: any }>
    ) {
      const { convId, entityId, key, item } = action.payload;
      const entity = state.byConvId[convId]?.byId[entityId];
      if (!entity) return;
      const existing = (entity.props as any)[key];
      if (Array.isArray(existing)) {
        (entity.props as any)[key] = [...existing, item];
      } else if (existing === undefined) {
        (entity.props as any)[key] = [item];
      } else {
        (entity.props as any)[key] = [existing, item];
      }
    },

    // Upsert entity (create if missing, else shallow-merge props)
    upsertEntity(state, action: PayloadAction<{ convId: string; entity: TimelineEntity }>) {
      const { convId, entity } = action.payload;
      const conv = ensureConversation(state, convId);
      const existing = conv.byId[entity.id];
      if (!existing) {
        conv.byId[entity.id] = entity;
        conv.order.push(entity.id);
        return;
      }
      // Only merge if kinds match; otherwise replace the entity
      if (existing.kind === entity.kind) {
        // Type-safe merge: both entities have the same kind, so props are compatible
        mergeEntityProps(existing as typeof entity, entity);
      } else {
        // Different kind: replace the entity entirely
        conv.byId[entity.id] = entity;
      }
    },

    // Append text to a message entity (streaming)
    appendMessageText(
      state,
      action: PayloadAction<{ convId: string; messageId: string; text: string }>
    ) {
      const { convId, messageId, text } = action.payload;
      const entity = state.byConvId[convId]?.byId[messageId] as MessageEntity | undefined;

      if (entity && entity.kind === 'message') {
        entity.props.content = (entity.props.content || '') + text;
        entity.props.streaming = true;
      } else if (!entity) {
        // Create new streaming message entity
        const newEntity: MessageEntity = {
          id: messageId,
          kind: 'message',
          timestamp: Date.now(),
          props: {
            role: 'assistant',
            content: text,
            streaming: true,
          },
        };

        const conv = ensureConversation(state, convId);
        conv.byId[messageId] = newEntity;
        conv.order.push(messageId);
      }
    },

    // Finalize a streaming message
    finalizeMessage(
      state,
      action: PayloadAction<{ convId: string; messageId: string; fullText?: string }>
    ) {
      const { convId, messageId, fullText } = action.payload;
      const entity = state.byConvId[convId]?.byId[messageId] as MessageEntity | undefined;

      if (entity && entity.kind === 'message') {
        if (fullText !== undefined) {
          entity.props.content = fullText;
        }
        entity.props.streaming = false;
      }
    },

    // Update tool progress
    updateToolProgress(
      state,
      action: PayloadAction<{
        convId: string;
        toolId: string;
        progress: number;
        status?: 'error' | 'completed' | 'running' | 'queued';
      }>
    ) {
      const { convId, toolId, progress, status } = action.payload;
      const entity = state.byConvId[convId]?.byId[toolId];

      if (entity && entity.kind === 'tool_call') {
        entity.props.progress = progress;
        if (status) {
          entity.props.status = status;
        }
      }
    },

    // Remove an entity from a conversation timeline
    removeEntity(state, action: PayloadAction<{ convId: string; entityId: string }>) {
      const { convId, entityId } = action.payload;
      const conv = state.byConvId[convId];
      if (!conv) return;
      if (!conv.byId[entityId]) return;
      delete conv.byId[entityId];
      conv.order = conv.order.filter((id) => id !== entityId);
    },

    // Remove all entities of a given kind from a conversation timeline
    removeEntitiesByKind(
      state,
      action: PayloadAction<{ convId: string; kind: TimelineEntity['kind'] }>
    ) {
      const { convId, kind } = action.payload;
      const conv = state.byConvId[convId];
      if (!conv) return;
      const keepOrder: string[] = [];
      for (const id of conv.order) {
        const ent = conv.byId[id];
        if (!ent) continue;
        if (ent.kind === kind) {
          delete conv.byId[id];
          continue;
        }
        keepOrder.push(id);
      }
      conv.order = keepOrder;
    },

    // Clear conversation
    clearConversation(state, action: PayloadAction<string>) {
      delete state.byConvId[action.payload];
      if (state.activeConvId === action.payload) {
        state.activeConvId = null;
      }
    },

    // Move timeline entities from one conversation id to another.
    // Used when the server assigns a canonical conversation id after optimistic local creation.
    migrateConversation(state, action: PayloadAction<{ fromConvId: string; toConvId: string }>) {
      const { fromConvId, toConvId } = action.payload;
      if (!fromConvId || !toConvId || fromConvId === toConvId) {
        return;
      }

      const fromConv = state.byConvId[fromConvId];
      if (!fromConv) {
        return;
      }

      const toConv = ensureConversation(state, toConvId);

      for (const entityId of fromConv.order) {
        const entity = fromConv.byId[entityId];
        if (!entity || toConv.byId[entityId]) {
          continue;
        }
        toConv.byId[entityId] = entity;
        toConv.order.push(entityId);
      }

      delete state.byConvId[fromConvId];
      if (state.activeConvId === fromConvId) {
        state.activeConvId = toConvId;
      }
    },

    // Clear all
    clearAll(state) {
      state.byConvId = {};
      state.activeConvId = null;
    },
  },
});

export const {
  setActiveConversation,
  setConversationDisplayMode,
  addEntity,
  addEntities,
  updateEntity,
  appendEntityArrayItem,
  upsertEntity,
  appendMessageText,
  finalizeMessage,
  updateToolProgress,
  removeEntity,
  removeEntitiesByKind,
  clearConversation,
  migrateConversation,
  clearAll,
} = timelineSlice.actions;

export default timelineSlice.reducer;

// Selectors

// Get ordered entities for a conversation
// Memoized selector per conversation to avoid churn of new arrays each render
const entitiesSelectorCache = new Map<string, (state: RootState) => TimelineEntity[]>();
export const selectConversationEntities = (convId: string | null) => {
  if (!convId) return () => [];
  const cached = entitiesSelectorCache.get(convId);
  if (cached) return cached;
  const selector: (state: RootState) => TimelineEntity[] = createSelector(
    [(state: RootState) => state.timeline?.byConvId[convId]],
    (conv) => {
      if (!conv) return [] as TimelineEntity[];
      return conv.order.map((id) => conv.byId[id]).filter(Boolean);
    }
  );
  entitiesSelectorCache.set(convId, selector);
  return selector;
};

// Get active conversation entities
// Memoized to avoid unnecessary rerenders when the same data is returned
export const selectActiveEntities = createSelector(
  [
    (state: RootState) => state.timeline?.activeConvId,
    (state: RootState) => state.timeline?.byConvId,
  ],
  (activeConvId, byConvId) => {
    if (!activeConvId || !byConvId) return [] as TimelineEntity[];
    const conv = byConvId[activeConvId];
    if (!conv) return [] as TimelineEntity[];
    return conv.order.map((id) => conv.byId[id]).filter(Boolean);
  }
);

// Get active conversation ID
export const selectActiveConvId = (state: RootState) => {
  return state.timeline?.activeConvId || null;
};

/**
 * Get conversation display mode (defaults to 'chat').
 * - 'chat': Summary widgets are visible in chat timeline
 * - 'ui': Summary widgets are hidden in chat (shown in UI instead)
 *
 * @param convId - Conversation ID. If not provided, returns a selector that always returns 'chat'
 * @returns A memoized selector function for the conversation's display mode
 */
const displayModeSelectorCache = new Map<string, (state: RootState) => 'chat' | 'ui'>();
const DEFAULT_DISPLAY_MODE: 'chat' = 'chat';
const defaultDisplayModeSelector = () => DEFAULT_DISPLAY_MODE;

export const selectConversationDisplayMode = (convId?: string | null) => {
  if (!convId) return defaultDisplayModeSelector;
  const cached = displayModeSelectorCache.get(convId);
  if (cached) return cached;
  // Simple selector without createSelector to avoid identity function warning
  // The displayMode is a primitive value, so reference equality is guaranteed
  const selector = (state: RootState): 'chat' | 'ui' =>
    state.timeline?.byConvId[convId]?.displayMode ?? DEFAULT_DISPLAY_MODE;
  displayModeSelectorCache.set(convId, selector);
  return selector;
};

// Get a specific entity
export const selectEntity = (convId: string, entityId: string) => (state: RootState) => {
  return state.timeline?.byConvId[convId]?.byId[entityId];
};

// Check if any message is streaming
// Memoized hasStreaming selector per conversation
const hasStreamingSelectorCache = new Map<string, (state: RootState) => boolean>();
export const selectHasStreamingMessages = (convId: string) => {
  const cached = hasStreamingSelectorCache.get(convId);
  if (cached) return cached;
  const selector: (state: RootState) => boolean = createSelector(
    [(state: RootState) => state.timeline?.byConvId[convId]],
    (conv) => {
      if (!conv) return false;
      return Object.values(conv.byId).some(
        (entity) => entity.kind === 'message' && (entity as MessageEntity).props.streaming
      );
    }
  );
  hasStreamingSelectorCache.set(convId, selector);
  return selector;
};

// Check if any tool call is running or queued for a conversation
const hasRunningToolsSelectorCache = new Map<string, (state: RootState) => boolean>();
export const selectHasRunningTools = (convId: string) => {
  const cached = hasRunningToolsSelectorCache.get(convId);
  if (cached) return cached;
  const selector: (state: RootState) => boolean = createSelector(
    [(state: RootState) => state.timeline?.byConvId[convId]],
    (conv) => {
      if (!conv) return false;
      return Object.values(conv.byId).some((entity) => {
        if (entity.kind !== 'tool_call') return false;
        const status = (entity as any).props?.status;
        return status === 'running' || status === 'queued';
      });
    }
  );
  hasRunningToolsSelectorCache.set(convId, selector);
  return selector;
};

// Aggregate busy selector: streaming messages or running tools
const isBusySelectorCache = new Map<string, (state: RootState) => boolean>();
export const selectIsConversationBusy = (convId: string) => {
  const cached = isBusySelectorCache.get(convId);
  if (cached) return cached;
  const selector: (state: RootState) => boolean = createSelector(
    [
      (state: RootState) => selectHasStreamingMessages(convId)(state),
      (state: RootState) => selectHasRunningTools(convId)(state),
    ],
    (hasStreaming: boolean, hasRunningTools: boolean): boolean =>
      Boolean(hasStreaming || hasRunningTools)
  );
  isBusySelectorCache.set(convId, selector);
  return selector;
};
